/**
 * @fileoverview Static navigation configurations for the application, including
 * main navigation items and admin table navigation structure. Table navigation
 * is derived from the centralized entity registry.
 */

import React from 'react';
import { HiOutlineInboxArrowDown } from 'react-icons/hi2';
import { PiCalendarCheck, PiCalendarDots, PiChartBar, PiHouse, PiQuestion, PiScales, PiTable } from 'react-icons/pi';
import type { Navigation, NavigationItem } from '@/app/providers/AppProvider';
import { ENTITY_KEYS, ENTITY_REGISTRY } from '../features/admin/registry';

/**
 * Creates a navigation item for a table entity from the registry.
 */
function createTableNavItem(entityKey: string): NavigationItem {
  const config = ENTITY_REGISTRY[entityKey as keyof typeof ENTITY_REGISTRY];
  return {
    segment: entityKey,
    title: entityKey,
    // Pattern is relative to computed path (/tables/{entity}), so only dynamic segments needed
    pattern: `${entityKey}{/:${config.paramName}}{/:action}`,
  };
}

export const STATIC_NAVIGATION: Navigation = [
  {
    segment: '',
    title: 'Home',
    icon: React.createElement(PiHouse, { size: 20 }),
    pattern: '',
  },
  {
    kind: 'header',
    title: 'Capture',
  },
  {
    segment: 'inbox',
    title: 'Inbox',
    icon: React.createElement(HiOutlineInboxArrowDown, { size: 20 }),
    pattern: 'inbox{/:noteId}',
  },
  {
    segment: 'daily-notes',
    title: 'Daily Review',
    icon: React.createElement(PiCalendarCheck, { size: 21 }),
    pattern: 'daily-notes{/:noteId}',
  },
  {
    segment: 'weekly-review',
    title: 'Weekly Review',
    icon: React.createElement(PiCalendarDots, { size: 21 }),
    pattern: 'weekly-review',
  },
  {
    segment: 'weight',
    title: 'Weight',
    icon: React.createElement(PiScales, { size: 21 }),
    pattern: 'weight',
  },
];

export const ADMIN_NAVIGATION: Navigation = [
  {
    kind: 'header',
    title: 'Administration',
  },
  {
    segment: 'documentation',
    title: 'Documentation',
    icon: React.createElement(PiQuestion, { size: 22 }),
    pattern: 'documentation{/:noteId}',
  },
  {
    segment: 'code-health',
    title: 'Code Health',
    icon: React.createElement(PiChartBar, { size: 22 }),
    pattern: 'code-health',
  },
  {
    segment: 'tables',
    title: 'Tables',
    icon: React.createElement(PiTable, { size: 22 }),
    pattern: 'tables',
    children: ENTITY_KEYS.map(createTableNavItem),
  },
];
